// Lightweight Google reCAPTCHA v3 loader and executor
// Usage: await getRecaptchaToken('action-name')

const RECAPTCHA_API_SRC_BASE = 'https://www.google.com/recaptcha/api.js?render='

let scriptAppended = false
let pendingLoads = []

const appendRecaptchaScript = siteKey =>
    new Promise(resolve => {
        if (scriptAppended) {
            resolve()
            return
        }
        scriptAppended = true
        const script = document.createElement('script')
        script.src = `${RECAPTCHA_API_SRC_BASE}${encodeURIComponent(siteKey)}`
        script.async = true
        script.defer = true
        script.onload = () => resolve()
        script.onerror = () => resolve() // fail open: resolve to allow app to keep working
        document.head.appendChild(script)
    })

const waitForGrecaptchaReady = () =>
    new Promise(resolve => {
        if (window.grecaptcha && window.grecaptcha.ready) {
            window.grecaptcha.ready(() => resolve())
            return
        }

        // If grecaptcha not present yet, poll briefly
        const start = Date.now()
        const interval = setInterval(() => {
            if (window.grecaptcha && window.grecaptcha.ready) {
                clearInterval(interval)
                window.grecaptcha.ready(() => resolve())
            } else if (Date.now() - start > 5000) {
                clearInterval(interval)
                resolve()
            }
        }, 50)
    })

export const ensureRecaptchaLoaded = async () => {
    try {
        const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY
        if (!siteKey) return false
        await appendRecaptchaScript(siteKey)
        await waitForGrecaptchaReady()
        if (window.grecaptcha && window.grecaptcha.execute) {
            // Trigger an initial execute to make sure the badge appears
            try {
                await window.grecaptcha.execute(siteKey, { action: 'page_load' })
            } catch (err) {
                // ignore token result; this is only to display the badge
            }
            return true
        }
        return false
    } catch {
        return false
    }
}

export const getRecaptchaToken = async (action = 'request') => {
    try {
        const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY
        if (!siteKey) {
            return null
        }

        // Avoid concurrent duplicate loads
        if (!scriptAppended) {
            const loadPromise = appendRecaptchaScript(siteKey)
            pendingLoads.push(loadPromise)
            await loadPromise
            pendingLoads = []
        } else if (pendingLoads.length) {
            await Promise.allSettled(pendingLoads)
        }

        await waitForGrecaptchaReady()

        if (!window.grecaptcha || !window.grecaptcha.execute) {
            return null
        }

        const token = await window.grecaptcha.execute(siteKey, { action })
        return token || null
    } catch (err) {
        return null
    }
}

/**
 * Adjusts reCAPTCHA badge position to prevent overlap with content
 * This function finds the reCAPTCHA badge and ensures it doesn't overlap with bottom elements
 */
export const adjustRecaptchaBadgePosition = () => {
    if (typeof window === 'undefined') return

    // Find the reCAPTCHA badge element
    const findBadge = () => {
        // reCAPTCHA badge is typically in an iframe or div with specific classes
        const badge = document.querySelector('.grecaptcha-badge')
        if (badge) return badge

        // Alternative: find by position fixed and bottom style
        const allDivs = document.querySelectorAll('div[style*="position: fixed"]')
        const foundDiv = Array.from(allDivs).find((div) => {
            const style = div.getAttribute('style') || ''
            return style.includes('bottom') && style.includes('right')
        })
        return foundDiv || null
    }

    // Apply styles to prevent overlap
    const applyBadgeStyles = () => {
        const badge = findBadge()
        if (badge) {
            // Ensure badge has proper z-index and doesn't interfere
            badge.style.zIndex = '999'
            badge.style.visibility = 'visible'
            badge.style.opacity = '1'
        }
    }

    // Try immediately
    applyBadgeStyles()

    // Also try after a delay in case badge loads later
    setTimeout(applyBadgeStyles, 1000)
    setTimeout(applyBadgeStyles, 3000)
}

export default getRecaptchaToken;

// Proactively load the script on first import so the v3 badge is visible
(async () => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY
    if (typeof window !== 'undefined' && siteKey) {
        try {
            await appendRecaptchaScript(siteKey)
            // Adjust badge position after loading
            setTimeout(adjustRecaptchaBadgePosition, 500)
        } catch (err) {
            // ignore
        }
    }
})();
